import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from './Avatar.tsx';
import { ConversationRow } from './ConversationRow.tsx';
import { G } from './icons.tsx';
import type { ChatConversation } from './types.js';
import { useT } from '../i18n/I18n.tsx';

/**
 * The conversation list column.
 *
 * Header, search, filters and rows — the structure of every messenger, because
 * it is the structure that works. Search filters locally on names this browser
 * can actually read; there is no server-side search, and there could not be:
 * the server cannot read a conversation's name.
 */

export type ListFilter = 'all' | 'unread' | 'active' | 'ended';

const FILTERS: Array<{
  id: ListFilter;
  key:
    | 'messenger.filterAll'
    | 'messenger.filterUnread'
    | 'messenger.filterActive'
    | 'messenger.filterEnded';
}> = [
  { id: 'all', key: 'messenger.filterAll' },
  { id: 'unread', key: 'messenger.filterUnread' },
  { id: 'active', key: 'messenger.filterActive' },
  { id: 'ended', key: 'messenger.filterEnded' },
];

export interface MenuItem {
  label: string;
  glyph: Parameters<typeof G>[0]['n'];
  onSelect: () => void;
  danger?: boolean;
}

export function ConversationList({
  conversations,
  selectedId,
  ownerName,
  loading,
  creating,
  onSelect,
  onNewChat,
  menu,
}: {
  conversations: ChatConversation[];
  selectedId?: string | null;
  ownerName: string;
  loading?: boolean;
  /** A link is being created; the control reflects it rather than sitting inert. */
  creating?: boolean;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  menu: MenuItem[];
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ListFilter>('all');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBox = useRef<HTMLDivElement>(null);
  /** Nothing exists yet, so the one thing worth doing should be obvious. */
  const empty = !loading && conversations.length === 0;

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (!menuBox.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [menuOpen]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return conversations.filter((conversation) => {
      if (needle && !conversation.name.toLowerCase().includes(needle)) return false;
      if (filter === 'unread') return conversation.unread > 0;
      if (filter === 'active') return conversation.status === 'active';
      if (filter === 'ended') return conversation.status === 'expired';
      return true;
    });
  }, [conversations, filter, query]);

  return (
    <aside className="msgr__sidebar">
      <header className="sidebar-head">
        <Avatar name={ownerName} size="sm" />
        <div className="sidebar-head__actions" ref={menuBox}>
          {/*
            Labelled, not an icon alone.

            An icon on its own is discoverable only to someone who already knows
            what it does, and creating a link is the one action a new owner has
            to find before the product does anything at all. It also asks for
              attention while there is nothing else on the page — see
            `.newlink--calling` for why that stops as soon as it is earned.
          */}
          <button
            type="button"
            className={empty ? 'newlink newlink--calling' : 'newlink'}
            onClick={onNewChat}
            disabled={creating}
            title={t('messenger.newConversationHint')}
          >
            <G n={creating ? 'clock' : 'link'} size={18} />
            <span>{creating ? t('messenger.creatingLink') : t('messenger.newConversation')}</span>
          </button>
          <button
            type="button"
            className="ibtn"
            onClick={() => setMenuOpen((value) => !value)}
            aria-label={t('common.menu')}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <G n="menu" size={22} />
          </button>
          {menuOpen ? (
            <div className="menu" role="menu">
              {menu.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  className={item.danger ? 'menu--danger' : undefined}
                  onClick={() => {
                    setMenuOpen(false);
                    item.onSelect();
                  }}
                >
                  <G n={item.glyph} size={19} />
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </header>

      <div className="sidebar-search">
        <div className="sidebar-search__box">
          <G n="search" size={18} />
          <input
            type="search"
            value={query}
            aria-label={t('common.search')}
            placeholder={t('messenger.searchPlaceholder')}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query ? (
            <button
              type="button"
              className="ibtn ibtn--sm"
              onClick={() => setQuery('')}
              aria-label={t('messenger.clearSearch')}
            >
              <G n="close" size={16} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="sidebar-filters" role="group" aria-label={t('common.search')}>
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={filter === item.id ? 'chip is-active' : 'chip'}
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {t(item.key)}
          </button>
        ))}
      </div>

      <div className="conv-scroll">
        {loading ? (
          <div className="conv-empty">{t('messenger.loadingList')}</div>
        ) : visible.length === 0 ? (
          <div className="conv-empty">
            <G n="chat-empty" size={64} className="no-chat__art" />
            {conversations.length === 0 ? (
              <>
                <strong>{t('messenger.emptyTitle')}</strong>
                <span>{t('messenger.emptyBody')}</span>
              </>
            ) : (
              <span>{t('messenger.noMatch')}</span>
            )}
          </div>
        ) : (
          visible.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              selected={conversation.id === selectedId}
              onSelect={() => onSelect(conversation.id)}
            />
          ))
        )}
      </div>
    </aside>
  );
}
